import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import JisEmptyList from "../../components/controls/emptylist";
import PdfViewer from "../../components/controls/pdfviewer";
import { api, docLink, fileToBase64, fmtDate, fmtDateTime, money, reqTypeTitle, tone } from "./hctc_common";
import "./hctc.css";

// One application. Applicants see progress, pay and download; desk officers get the actions their screens allow.
const CATEGORY_TITLES = {
  application_form: "Generated application form",
  application_letter: "Application letter",
  supporting: "Supporting documents",
  historical_support: "Historical case supporting documents",
  original_document: "Original document",
  photocopy: "Photocopy to be certified",
  record: "Records obtained",
  search_report: "Search report",
  other: "Other",
  certified: "Certified true copies",
};

export default function ServiceAppDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesk = location.pathname.startsWith("/service-desk");
  const { token, profile, setIsExpired } = useContext(ShareContext);
  const [cfg, setCfg] = useState(null);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dlg, setDlg] = useState(null);
  const [preview, setPreview] = useState("");
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    const [x, c] = await Promise.all([api(token, setIsExpired, `serviceapp/${id}`), cfg ? Promise.resolve(null) : api(token, setIsExpired, "serviceapp/config")]);
    setLoading(false);
    if (c?.code === 0) setCfg(c);
    if (x.code === 0) setD(x);
    else if (x.code !== -10) setErr(x.status || "Unable to load the application");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const post = async (path, body, ok) => {
    setErr("");
    setMsg("");
    setLoading(true);
    const r = await api(token, setIsExpired, path, { method: "POST", body });
    setLoading(false);
    if (r.code === 0) {
      setDlg(null);
      setMsg(ok || "Done");
      load();
      return r;
    }
    if (r.code !== -10) setErr(r.status || "The action failed");
    return null;
  };

  const downloadDoc = async (doc) => {
    setLoading(true);
    try {
      const resp = await fetch(`${Constants.apiUrl}/serviceapp/download/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.status || "Download failed");
      }
      const blob = await resp.blob();
      const href = window.URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = href;
      el.download = `${doc.title}.pdf`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      window.URL.revokeObjectURL(href);
      load();
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  if (!d) {
    return (
      <div className="hctc-page">
        {err && <div className="hctc-err">{err}</div>}
        <BackLoader open={loading} />
      </div>
    );
  }

  const a = d.item;
  const staff = d.isStaff && isDesk;
  const kv = (k, v) =>
    v || v === 0 ? (
      <>
        <div className="k">{k}</div>
        <div className="v">{v}</div>
      </>
    ) : null;
  const grouped = Object.keys(CATEGORY_TITLES)
    .map((c) => [c, d.documents.filter((x) => x.category === c)])
    .filter(([, list]) => list.length > 0);
  const staffCategory = (c) => ["record", "search_report", "other"].includes(c);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <JisButton variant="plain" onClick={() => navigate(-1)}>
            Back
          </JisButton>
          <div>
            <h2>
              {a.applicationNumber || `Draft #${a.id}`} <span className="hctc-muted">{reqTypeTitle(cfg, a.requestType)}</span>
            </h2>
            <div>
              <span className={`hctc-pill ${tone(a.status)}`}>{a.status}</span>
              <span className={`hctc-pill ${tone(a.classification)}`}>{a.classification}</span>
              {a.histVerificationStatus && <span className={`hctc-pill ${tone(a.histVerificationStatus)}`}>Historical verification: {a.histVerificationStatus}</span>}
              {a.trackingNumber && <span className="hctc-pill plain">Tracking {a.trackingNumber}</span>}
            </div>
          </div>
        </div>
        <div className="right hctc-actions">
          {d.isOwner && (a.status === "Draft" || a.status === "Rework Requested") && (
            <JisButton onClick={() => navigate(`/service-applications/apply/${a.id}`)}>{a.status === "Draft" ? "Continue" : "Make corrections"}</JisButton>
          )}
          {d.isOwner && a.status === "Awaiting Payment" && <JisButton onClick={() => setPaying(true)}>Pay {money(a.amount)}</JisButton>}
        </div>
      </div>
      {msg && <div className="hctc-ok">{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}
      {d.isOwner && a.status === "Rework Requested" && <div className="hctc-warn">Rework requested: {a.lastComment}</div>}
      {d.isOwner && ["Record Not Found", "Pending Archive Retrieval", "Pending Digitization", "Partially Available"].includes(a.status) && (
        <div className="hctc-warn">
          {a.status}. {a.lastComment}
        </div>
      )}

      {staff && d.actions.length > 0 && (
        <div className="hctc-card">
          <div className="head">
            <h3>Actions</h3>
          </div>
          <div className="hctc-actions">
            {d.actions.map((x) => (
              <JisButton key={x.code} variant={x.style === "danger" ? "outline" : x.style} onClick={() => setDlg({ kind: "act", act: x })}>
                {x.label}
              </JisButton>
            ))}
          </div>
        </div>
      )}

      {staff && d.canEditDocs && (
        <div className="hctc-card">
          <div className="head">
            <h3>Records</h3>
          </div>
          <div className="hctc-actions">
            <JisButton variant="outline" onClick={() => setDlg({ kind: "upload" })}>
              Upload record (option A)
            </JisButton>
            {(a.caseId || a.historicalCaseId) && (
              <JisButton variant="outline" onClick={() => setDlg({ kind: "repo" })}>
                Select from JIS / digitized historical records (B, C)
              </JisButton>
            )}
            <JisButton variant="outline" onClick={() => setDlg({ kind: "link" })}>
              Link historical / JIS record
            </JisButton>
            {!a.historicalCaseId && cfg?.desks?.some((x) => x.code === "svc_hist_verify") && a.classification !== "JIS" && (
              <JisButton variant="plain" onClick={() => setDlg({ kind: "register" })}>
                Register as new historical record
              </JisButton>
            )}
          </div>
        </div>
      )}

      <div className="hctc-grid wide">
        <div className="hctc-card">
          <div className="head">
            <h3>Applicant (A)</h3>
          </div>
          <div className="hctc-kv">
            {kv("Applicant type", a.applicantType)}
            {kv("Name", a.fullName)}
            {kv("Law firm", a.lawFirmName)}
            {kv("Enrollment no.", a.enrollmentNumber)}
            {kv("Email", a.email)}
            {kv("Mobile", a.mobile)}
            {kv("Address", a.address)}
          </div>
        </div>
        <div className="hctc-card">
          <div className="head">
            <h3>Request (B, C)</h3>
          </div>
          <div className="hctc-kv">
            {kv("Service", reqTypeTitle(cfg, a.requestType))}
            {kv("Case number", a.caseNumber)}
            {kv("Suit number", a.suitNumber)}
            {kv("Legacy case number", a.legacyCaseNumber)}
            {kv(a.requestType === "non_case" ? "Description" : "Case title", a.caseTitle)}
            {kv("Original filing date", a.originalFilingDate ? fmtDate(a.originalFilingDate) : a.requestType !== "non_case" && a.requestType !== "case_search" ? "Unknown" : null)}
            {kv("Division", d.names.division)}
            {kv("Court", d.names.court)}
            {kv("Judge", Constants.judgeTitle(a.judgeName))}
            {kv("Registrar", a.registrarName)}
            {kv("Physical file", a.physicalFileNumber)}
            {kv("Archive reference", a.archiveReferenceNumber)}
            {kv("Judgment date", a.judgmentDate && fmtDate(a.judgmentDate))}
            {kv("Period", a.startDate && `${fmtDate(a.startDate)} - ${fmtDate(a.endDate)}`)}
            {kv("Days requested / approved", a.daysRequested && `${a.daysRequested} / ${a.daysApproved ?? "-"}`)}
            {kv("Search period", a.searchStartYear && `${a.searchStartYear} - ${a.searchEndYear}${a.isHistoricalSearch ? " (historical search)" : ""}`)}
            {kv("Party name", a.searchPartyName)}
            {kv("Purpose", a.purpose)}
            {kv("Remarks", a.remarks)}
          </div>
          {d.searchCoverage && (
            <div className="hctc-info">
              Coverage: {d.searchCoverage.totalYears} year(s), {d.searchCoverage.historicalYears} historical. Sources: {d.searchCoverage.sources.join(", ")}.
            </div>
          )}
        </div>
        {(d.historical || d.jisCase) && (
          <div className="hctc-card">
            <div className="head">
              <h3>Linked record</h3>
            </div>
            <div className="hctc-kv">
              {d.jisCase && kv("JIS case", `${d.jisCase.caseNumber || ""} (${d.jisCase.referenceNumber})${d.jisCase.isHistorical ? " - historical" : ""}`)}
              {d.historical && (
                <>
                  {kv(
                    "Historical record",
                    staff ? (
                      <a href={`/historical-cases/${d.historical.id}`} target="_blank" rel="noreferrer">
                        {d.historical.historicalRef}
                      </a>
                    ) : (
                      d.historical.historicalRef
                    ),
                  )}
                  {kv("Original number", d.historical.originalCaseNumber)}
                  {kv("Filed", d.historical.originalFilingDate ? fmtDate(d.historical.originalFilingDate) : "Unknown")}
                  {kv("Verification", <span className={`hctc-pill ${tone(d.historical.verificationStatus)}`}>{d.historical.verificationStatus}</span>)}
                  {kv("Availability", d.historical.availabilityStatus)}
                  {kv("Digitization", d.historical.digitizationStatus)}
                  {staff && kv("Record source", d.historical.recordSource)}
                  {staff && kv("Physical file / archive", [d.historical.physicalFileNumber, d.historical.archiveReferenceNumber, d.historical.archiveLocation].filter(Boolean).join(" / "))}
                </>
              )}
              {kv("Record obtained from", a.recordSourceObtained)}
            </div>
          </div>
        )}
        {(a.amount !== null && a.amount !== undefined) && (
          <div className="hctc-card">
            <div className="head">
              <h3>Assessment and payment</h3>
            </div>
            <div className="hctc-kv">
              {kv("Units", `${a.units} ${d.fee?.unit || ""}`)}
              {kv("Rate", money(a.rate))}
              {kv("Total pages", a.totalPages)}
              {kv("Historical document pages", a.requestType !== "case_search" ? a.historicalPages : null)}
              {kv("Internal slip", money(a.internalAmount))}
              {kv("External slip", money(a.externalAmount))}
              {kv("Amount due", <b>{money(a.amount)}</b>)}
              {kv("Assessed", fmtDateTime(a.assessedOn))}
              {kv("Payment", a.isPaid ? `Paid ${fmtDateTime(a.paidOn)}` : "Not paid")}
              {d.payment && kv("Bill reference", d.payment.billRef)}
            </div>
            {d.fee?.lines?.length > 0 && (
              <table className="case-table" style={{ marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Slip</th>
                    <th>Per unit</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {d.fee.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.title}</td>
                      <td>{l.itemType}</td>
                      <td>{money(l.perUnit)}</td>
                      <td>{money(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {d.payment?.isPaid && (
              <div className="hctc-actions" style={{ marginTop: 10 }}>
                <JisButton variant="plain" onClick={() => setPreview(`${Constants.baseurl}/receipt-internal/${d.payment.billRef}`)}>
                  Judiciary receipt
                </JisButton>
              </div>
            )}
          </div>
        )}
        {a.verificationNumber && (
          <div className="hctc-card">
            <div className="head">
              <h3>Certification</h3>
            </div>
            <div className="hctc-kv">
              {kv("Verification number", a.verificationNumber)}
              {kv("Certified by", d.names.certifiedBy)}
              {kv("Certified on", fmtDateTime(a.certifiedOn))}
              {kv("Verify online", <a href={`/verify-certified/${a.verificationNumber}`} target="_blank" rel="noreferrer">Open verification page</a>)}
            </div>
          </div>
        )}
      </div>

      <div className="hctc-card">
        <div className="head">
          <h3>Documents (D)</h3>
        </div>
        {grouped.length === 0 && <JisEmptyList title="No documents" />}
        {grouped.map(([cat, list]) => (
          <div key={cat} style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, margin: "6px 0" }}>{CATEGORY_TITLES[cat]}</div>
            <table className="case-table hctc-table">
              <tbody>
                {list.map((x) => (
                  <tr key={x.id}>
                    <td className="wrap">{x.title}</td>
                    <td>{x.pageCount} page(s)</td>
                    <td>{x.source}</td>
                    <td className="hctc-muted">
                      {fmtDate(x.createdOn)} {staff && x.uploadedBy}
                    </td>
                    {staff && (
                      <td>
                        {cat !== "certified" && cat !== "application_form" && (
                          <label className="hctc-check">
                            <input
                              type="checkbox"
                              checked={!!x.isForCertification}
                              disabled={!d.canEditDocs}
                              onChange={(e) => post(`serviceapp/doc-flag/${x.id}`, { forCertification: e.target.checked }, "Updated")}
                            />
                            To certify
                          </label>
                        )}
                      </td>
                    )}
                    <td>
                      <div className="hctc-row-actions">
                        {cat === "certified" ? (
                          <JisButton onClick={() => downloadDoc(x)}>Download</JisButton>
                        ) : (
                          <JisButton variant="plain" onClick={() => setPreview(docLink(x.url))}>
                            View
                          </JisButton>
                        )}
                        {staff && d.canEditDocs && staffCategory(cat) && (
                          <JisButton variant="plain" onClick={() => post(`serviceapp/remove-doc/${x.id}`, {}, "Removed")}>
                            Remove
                          </JisButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {d.instruments.length > 0 && (
        <div className="hctc-card">
          <div className="head">
            <h3>Media instruments applied (E)</h3>
          </div>
          <div className="hctc-grid">
            {d.instruments.map((m) => (
              <div key={m.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {/\.(png|jpe?g)$/i.test(m.snapshotPath || "") && <img className="hctc-thumb" src={docLink(m.snapshotPath)} alt={m.instrumentName} />}
                <div>
                  <div style={{ fontWeight: 600 }}>{cfg?.instrumentTypes?.find((t) => t.code === m.instrumentType)?.title || m.instrumentType}</div>
                  <div className="hctc-muted">{m.instrumentName}</div>
                  <div className="hctc-muted">{m.placement}</div>
                  {staff && <span className={`hctc-pill ${m.validation === "Valid" ? "good" : "warn"}`}>{m.validation}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="hctc-card">
        <div className="head">
          <h3>Progress</h3>
        </div>
        <ul className="hctc-timeline">
          {d.audit.map((x) => (
            <li key={x.id}>
              <div>
                <b>{x.action}</b>
                {x.newStatus && x.previousStatus !== x.newStatus && (
                  <span className="hctc-muted">
                    {" "}
                    {x.previousStatus || "-"} → {x.newStatus}
                  </span>
                )}
              </div>
              {x.comments && <div>{x.comments}</div>}
              <div className="when">
                {fmtDateTime(x.createdOn)} {x.userRole && `· ${x.userRole}`} {x.user && `· ${x.user}`} {x.ip && `· ${x.ip}`}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {dlg?.kind === "act" && <ActDialog d={d} act={dlg.act} token={token} setIsExpired={setIsExpired} onClose={() => setDlg(null)} onRun={(body) => post(`serviceapp/action/${id}`, body, dlg.act.label)} />}
      {dlg?.kind === "upload" && <StaffUpload sources={cfg?.documentSources || []} onClose={() => setDlg(null)} onRun={(body) => post(`serviceapp/staff-upload/${id}`, body, "Record uploaded")} />}
      {dlg?.kind === "repo" && <RepoDialog id={id} token={token} setIsExpired={setIsExpired} onClose={() => setDlg(null)} onRun={(body) => post(`serviceapp/attach/${id}`, body, "Record attached")} onPreview={setPreview} />}
      {dlg?.kind === "link" && <LinkDialog token={token} setIsExpired={setIsExpired} onClose={() => setDlg(null)} onRun={(body) => post(`serviceapp/link-record/${id}`, body, "Record linked")} />}
      {dlg?.kind === "register" && (
        <JisDialogForm title="Register historical record" onClose={() => setDlg(null)} minWidth="35%">
          <div className="hctc-dialog-body">
            <p>No matching record was found. Create a historical record (Pending Verification) from the details in this application and link it?</p>
            <div className="hctc-actions">
              <JisButton onClick={() => post(`serviceapp/register-historical/${id}`, {}, "Historical record registered")}>Register and link</JisButton>
              <JisButton variant="outline" onClick={() => setDlg(null)}>
                Cancel
              </JisButton>
            </div>
          </div>
        </JisDialogForm>
      )}
      {paying && <ServicePay app={a} token={token} profile={profile} setIsExpired={setIsExpired} onClose={() => setPaying(false)} onError={setErr} />}
      {preview && (
        <JisDialogForm title="Document" onClose={() => setPreview("")}>
          <div style={{ height: "80vh", minWidth: "min(900px, 85vw)" }}>
            <PdfViewer pdfUrl={preview} />
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}

function ActDialog({ d, act, token, setIsExpired, onClose, onRun }) {
  const a = d.item;
  const [comment, setComment] = useState("");
  const [registrars, setRegistrars] = useState(null);
  const [registrarId, setRegistrarId] = useState("");
  const [availability, setAvailability] = useState("Available Physically");
  const [verifyRecord, setVerifyRecord] = useState(false);
  const [pages, setPages] = useState(d.suggestedPages || a.totalPages || "");
  const [histPages, setHistPages] = useState(a.historicalPages || "");
  const [days, setDays] = useState(a.daysApproved || a.daysRequested || "");
  const [docIds, setDocIds] = useState(d.documents.filter((x) => x.isForCertification && x.category !== "certified").map((x) => x.id));
  const needsRegistrar = act.kind === "registrar" && !["non_case", "case_search"].includes(a.requestType);

  useEffect(() => {
    if (!needsRegistrar) return;
    (async () => {
      const r = await api(token, setIsExpired, `serviceapp/registrars/${a.id}`);
      if (r.code === 0) {
        setRegistrars(r.list || []);
        const pre = r.autoRegistrarId || r.list?.find((x) => x.suggested)?.id;
        if (pre) setRegistrarId(String(pre));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const required = act.kind === "comment_required";
  const can =
    (!required || comment.trim()) &&
    (!needsRegistrar || registrarId) &&
    (act.kind !== "assessment" || a.requestType === "case_search" || Number(pages) > 0) &&
    (act.kind !== "days" || Number(days) > 0) &&
    (act.kind !== "certify" || docIds.length > 0);

  const submit = () =>
    onRun({
      action: act.code,
      comment: comment || null,
      registrarId: registrarId ? Number(registrarId) : null,
      availabilityStatus: act.kind === "verify" ? availability : null,
      verifyRecord,
      totalPages: pages ? Number(pages) : null,
      historicalPages: histPages ? Number(histPages) : null,
      daysApproved: days ? Number(days) : null,
      documentIds: act.kind === "certify" ? docIds : null,
    });

  const certifiable = d.documents.filter((x) => !["certified", "application_form"].includes(x.category));

  return (
    <JisDialogForm title={act.label} onClose={onClose} minWidth="40%">
      <div className="hctc-dialog-body">
        {act.kind === "verify" && (
          <>
            {!d.historical && !d.jisCase && <div className="hctc-warn">Link the application to a historical record or a JIS case first (Records panel).</div>}
            {d.historical && !["Verified", "Partially Verified"].includes(d.historical.verificationStatus) && (
              <label className="hctc-check" style={{ marginBottom: 10 }}>
                <input type="checkbox" checked={verifyRecord} onChange={(e) => setVerifyRecord(e.target.checked)} />
                Also mark historical record {d.historical.historicalRef} as verified (needs the Historical: Verify function)
              </label>
            )}
            <div className="hctc-field">
              <label>Record availability</label>
              <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                {["Available Digitally", "Available Physically", "Partially Available", "Restricted"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
          </>
        )}
        {needsRegistrar && (
          <div className="hctc-field">
            <label>
              Registrar<span className="req">*</span>
            </label>
            {registrars === null ? (
              <div className="hctc-muted">Loading...</div>
            ) : (
              <select value={registrarId} onChange={(e) => setRegistrarId(e.target.value)}>
                <option value="">Select the registrar</option>
                {registrars.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r.division ? `- ${r.division}` : ""} {r.suggested ? "(case registrar)" : ""}
                  </option>
                ))}
              </select>
            )}
            <div className="hint">{a.judgeId ? "The case is assigned to a judge: the registrar is filled in where one is attached." : "The case is not assigned to a judge: select the registrar."}</div>
          </div>
        )}
        {act.kind === "assessment" && (
          <div className="hctc-grid">
            {a.requestType === "case_search" ? (
              <div className="hctc-field full">
                <div className="hctc-info">
                  {a.searchEndYear - a.searchStartYear + 1} year(s) ({a.searchStartYear} - {a.searchEndYear}) × rate per year.
                </div>
              </div>
            ) : (
              <>
                <div className="hctc-field">
                  <label>
                    Total pages<span className="req">*</span>
                  </label>
                  <input type="number" min="1" value={pages} onChange={(e) => setPages(e.target.value)} />
                  <div className="hint">Pages of the records marked "to certify": {d.suggestedPages}</div>
                </div>
                <div className="hctc-field">
                  <label>Historical document pages</label>
                  <input type="number" min="0" value={histPages} onChange={(e) => setHistPages(e.target.value)} />
                </div>
              </>
            )}
            <div className="hctc-field full hctc-muted">Rate: {d.item.rate ? money(d.item.rate) : "as configured on Service Rates"}. Total = units × rate.</div>
          </div>
        )}
        {act.kind === "days" && (
          <div className="hctc-field">
            <label>
              Days approved<span className="req">*</span>
            </label>
            <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} />
            <div className="hint">
              Requested: {a.daysRequested} ({fmtDate(a.startDate)} - {fmtDate(a.endDate)}). Sundays and public holidays are excluded. Amount = days × rate.
            </div>
          </div>
        )}
        {act.kind === "certify" && (
          <div>
            <div className="hctc-sub">Select the documents to certify. Each gets the CTC stamp, verification number and QR code. Applicant instruments are not used here.</div>
            {certifiable.map((x) => (
              <label key={x.id} className="hctc-check" style={{ marginBottom: 6 }}>
                <input type="checkbox" checked={docIds.includes(x.id)} onChange={(e) => setDocIds(e.target.checked ? [...docIds, x.id] : docIds.filter((i) => i !== x.id))} />
                {x.title} <span className="hctc-muted">({x.pageCount} pages, {x.source})</span>
              </label>
            ))}
            {d.historical && <div className="hctc-warn">Historical record: confirm the record source in the comment.</div>}
          </div>
        )}
        <div className="hctc-field" style={{ marginTop: 10 }}>
          <label>
            Comment{required && <span className="req">*</span>}
          </label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={submit} disabled={!can}>
            Confirm
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}

function StaffUpload({ sources, onClose, onRun }) {
  const [f, setF] = useState({ category: "record", title: "", source: "", forCertification: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const base64 = await fileToBase64(file);
    await onRun({ ...f, base64, fileName: file.name });
    setBusy(false);
  };
  return (
    <JisDialogForm title="Upload record" onClose={onClose} minWidth="40%">
      <div className="hctc-dialog-body">
        <div className="hctc-grid">
          <div className="hctc-field">
            <label>Type</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              <option value="record">Record (to certify)</option>
              <option value="search_report">Search report</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="hctc-field span2">
            <label>Title</label>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </div>
          <div className="hctc-field span2">
            <label>
              Obtained from<span className="req">*</span>
            </label>
            <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>
              <option value="">Select</option>
              {sources.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
            <label className="hctc-check">
              <input type="checkbox" checked={f.forCertification} onChange={(e) => setF({ ...f, forCertification: e.target.checked })} /> To certify
            </label>
          </div>
          <div className="hctc-field full">
            <label>
              File<span className="req">*</span>
            </label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={submit} disabled={!file || !f.source} loading={busy}>
            Upload
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}

function RepoDialog({ id, token, setIsExpired, onClose, onRun, onPreview }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      const x = await api(token, setIsExpired, `serviceapp/case-documents/${id}`);
      if (x.code === 0) setR(x);
      else setErr(x.status || "Unable to load the records");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const list = (title, rows, source) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, margin: "6px 0" }}>{title}</div>
      {rows.length === 0 ? (
        <div className="hctc-muted">None</div>
      ) : (
        <table className="case-table hctc-table">
          <tbody>
            {rows.map((x) => (
              <tr key={x.id}>
                <td className="wrap">
                  {x.title}
                  {x.documentType && <div className="hctc-muted">{x.documentType}</div>}
                </td>
                <td>{x.pageCount ? `${x.pageCount} pages` : fmtDate(x.createdOn)}</td>
                <td>{x.verificationStatus && <span className={`hctc-pill ${tone(x.verificationStatus)}`}>{x.verificationStatus}</span>}</td>
                <td>
                  <div className="hctc-row-actions">
                    <JisButton variant="plain" onClick={() => onPreview(docLink(x.url))}>
                      View
                    </JisButton>
                    <JisButton variant="plain" onClick={() => onRun({ source, refId: x.id, forCertification: true })}>
                      Attach
                    </JisButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
  return (
    <JisDialogForm title="Select existing records" onClose={onClose} minWidth="55%">
      <div className="hctc-dialog-body">
        {err && <div className="hctc-err">{err}</div>}
        {!r && !err && <div className="hctc-muted">Loading...</div>}
        {r && (
          <>
            {list("Option B: JIS repository (case documents)", r.jis, "jis")}
            {list("Option C: Digitized historical records", r.historical, "historical")}
          </>
        )}
      </div>
    </JisDialogForm>
  );
}

function LinkDialog({ token, setIsExpired, onClose, onRun }) {
  const [q, setQ] = useState("");
  const [party, setParty] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const search = async () => {
    setErr("");
    const r = await api(token, setIsExpired, "historical/unified-search", { method: "POST", body: { text: q || null, partyName: party || null } });
    if (r.code === 0) setRes(r.results || []);
    else setErr(r.status || "Search failed");
  };
  return (
    <JisDialogForm title="Link a record" onClose={onClose} minWidth="55%">
      <div className="hctc-dialog-body">
        <div className="hctc-grid">
          <div className="hctc-field span2">
            <label>Case / legacy / file number or title</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
          </div>
          <div className="hctc-field">
            <label>Party name</label>
            <input value={party} onChange={(e) => setParty(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
          </div>
          <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
            <JisButton onClick={search}>Search</JisButton>
          </div>
        </div>
        {err && <div className="hctc-err">{err}</div>}
        {res && res.length === 0 && <div className="hctc-muted">No record found.</div>}
        {res && res.length > 0 && (
          <table className="case-table hctc-table" style={{ marginTop: 10 }}>
            <tbody>
              {res.map((r) => (
                <tr key={r.key}>
                  <td>
                    {r.number}
                    <div className="hctc-muted">{r.reference}</div>
                  </td>
                  <td className="wrap">{r.title}</td>
                  <td>
                    {(r.labels || []).map((l) => (
                      <span key={l} className={`hctc-pill ${tone(l)}`}>
                        {l}
                      </span>
                    ))}
                  </td>
                  <td>
                    <JisButton variant="plain" onClick={() => onRun(r.source === "historical" ? { historicalCaseId: r.historicalCaseId } : { caseId: r.caseId })}>
                      Link
                    </JisButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </JisDialogForm>
  );
}

// Internal payment for a service application (bill note "svc-{id}"); the callback page returns here.
function ServicePay({ app, token, profile, setIsExpired, onClose, onError }) {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    const r = await api(token, setIsExpired, `serviceapp/initiate-pay/${app.id}`, { method: "POST" });
    setBusy(false);
    if (r.code !== 0) {
      onError(r.status || "Unable to start the payment");
      onClose();
      return;
    }
    const bill = `JISI${r.id}`;
    try {
      localStorage.setItem("internalid", bill);
      localStorage.setItem("serviceAppId", String(app.id));
    } catch (e) {
      /* storage blocked: the callback still verifies from the URL */
    }
    if (r.gateway === "e-Transact" && r.etrans_url) {
      window.location = r.etrans_url;
    } else if (r.gateway === "Paystack") {
      window.location = `${Constants.baseurl}/paystack.html?name=${encodeURIComponent(app.fullName || "")}&email=${encodeURIComponent(app.email || profile?.primaryEmail || "")}&phone=${encodeURIComponent(
        app.mobile || "",
      )}&returnurl=${Constants.payment_callback_internal}/&amount=${r.amount}&docref=${bill}`;
    } else {
      window.location = `https://usepay4it.com/payment/collection/mda?mda=${Constants.pay4it_key}&callbackUrl=${Constants.payment_callback_internal}&internalBillReference=${bill}`;
    }
  };
  return (
    <JisDialogForm title="Payment" minWidth="25%" onClose={onClose}>
      <div className="hctc-dialog-body" style={{ textAlign: "center" }}>
        <h3>Make payment</h3>
        <p>
          Total amount payable
          <br />
          <b style={{ fontSize: 22 }}>{money(app.amount)}</b>
        </p>
        <div className="hctc-muted" style={{ marginBottom: 12 }}>
          Internal slip {money(app.internalAmount)} · External slip {money(app.externalAmount)}
        </div>
        <JisButton onClick={pay} loading={busy}>
          Pay now
        </JisButton>
      </div>
    </JisDialogForm>
  );
}
